/**
 * Recepción de documentos — transferencias ANTERIORES al sistema de envíos.
 *
 * Antes de que existiera la cabecera de envío (enviodocumentos) cada documento
 * viajaba como un renglón suelto de `transferencias` (envio_id NULL, un
 * idtransaccion por documento). Esta pantalla sigue recibiendo esos renglones
 * mientras queden en vuelo; el hub de Inventario de Documentos la abre desde
 * Envíos / Recepción embebida en un Dialog fullScreen, solo si el backend
 * reporta transferencias legadas en tránsito. Las nuevas se reciben por
 * checklist en Envíos.
 *
 * Contrato con transferenciabackend.py (sin cambios):
 *   - /api/getlotetransf {sucursal, tipo}: renglones 'En progreso' hacia la
 *     sucursal (comparada por código) que no envió el propio usuario. Devuelve
 *     motor[] con id, idtransaccion, stransmisor, sreceptor, fechatransm,
 *     documento y jsondocumentos (JSON de {sucursalrecep, documento,
 *     entregado} que el backend agrega al blob del producto al recibir).
 *     Sin renglones responde 400 con "No hay transferencias…": aquí es el
 *     estado vacío, no un error.
 *   - /api/transferenciabackend_procesarrecepciondocumentos
 *     {sucursal, documentos:[renglón tal cual llegó], comentario, tipo}:
 *     marca Completado y agrega el documento al blob del producto.
 *   - /api/transferenciabackend_procesarrechazodocumentos (mismo body):
 *     borra el renglón en progreso y limpia sucursalmatriculaentregada; el
 *     backend no guarda el comentario del rechazo.
 *   - /api/listtransf {tipo, sucursal} (solo lectura, mejor esfuerzo): aporta
 *     transportista, comentario de envío y marca/color, que getlotetransf no
 *     devuelve. Si falla, esas columnas quedan en "—" y todo lo demás funciona.
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { useGlobalMDAlert } from "context";
import {
  FP,
  EstadoVacio,
  ConfirmarDialog,
  fpDataGridSx,
  fpMultilineInputSx,
} from "components/financepro";
import { useApi } from "lib/api";
import { labelTipo, nombreSucursal, safeParse } from "lib/documentosvehiculo";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}
SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

// getlotetransf responde 400 tanto sin renglones como por error real; el
// texto es la única forma de distinguirlos.
const esListaVacia = (mensaje) => /^no hay transferencias/i.test(String(mensaje || "").trim());

// Sucursal en formato mixto ("2" o "2-Veron"): se muestra el nombre si viene,
// si no el valor crudo.
const etiquetaSucursal = (valor) => nombreSucursal(valor) || String(valor || "—");

// Tipo de documento del renglón: la columna `documento` (documentos->>'$.documento')
// y, si viniera vacía, el JSON crudo de la transferencia.
const tipoDocumento = (row) => {
  if (row.documento) return String(row.documento).replace(/"/g, "");
  const doc = safeParse(row.jsondocumentos);
  return doc && typeof doc === "object" ? doc.documento || "" : "";
};

function Recepciondocumentos({ tipo }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const sucursal = localStorage.getItem("sucursal") || "";
  const [transferencias, setTransferencias] = useState([]);
  // Transportista, comentario y vehículo por id de renglón (viene de listtransf).
  const [extras, setExtras] = useState({});
  const [cargando, setCargando] = useState(false);
  const [comentario, setComentario] = useState("");
  // Renglones con una llamada en curso: su par de botones queda deshabilitado
  // para que un doble clic no dispare dos recepciones.
  const [procesando, setProcesando] = useState(new Set());
  const [rechazando, setRechazando] = useState(null);

  const cargarExtras = useCallback(async () => {
    try {
      const { ok, data } = await post("/api/listtransf", { tipo, sucursal });
      if (!ok) return;
      const mapa = {};
      (data.transacciones || []).forEach((t) => {
        mapa[t.id] = t;
      });
      setExtras(mapa);
    } catch (e) {
      // Enriquecimiento opcional: sin él la recepción sigue funcionando.
    }
  }, [post, tipo, sucursal]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { status, data } = await post("/api/getlotetransf", { sucursal, tipo });
      if (status === 200) {
        setTransferencias(data.motor || []);
        cargarExtras();
      } else {
        setTransferencias([]);
        if (!esListaVacia(data.Error)) {
          showAlert({
            message: data.Error || "No se pudieron cargar las transferencias",
            color: "error",
          });
        }
      }
    } catch (e) {
      setTransferencias([]);
      showAlert({ message: "No se pudo conectar con el servidor", color: "error" });
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, sucursal, tipo, cargarExtras]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const marcarProcesando = (id, activo) =>
    setProcesando((prev) => {
      const s = new Set(prev);
      if (activo) s.add(id);
      else s.delete(id);
      return s;
    });

  // Recibir y rechazar comparten body y manejo; solo cambia el endpoint y el
  // mensaje. El renglón se manda tal cual llegó de getlotetransf: el backend
  // lee idtransaccion, chasis y jsondocumentos de documentos[0].
  const procesar = async (transferencia, endpoint, mensajeOk) => {
    marcarProcesando(transferencia.idtransaccion, true);
    try {
      const { status, data } = await post(endpoint, {
        sucursal,
        documentos: [transferencia],
        comentario,
        tipo,
      });
      if (status === 200) {
        showAlert({ message: mensajeOk, color: "success" });
        setTransferencias((prev) =>
          prev.filter((t) => t.idtransaccion !== transferencia.idtransaccion)
        );
        setComentario("");
        cargar();
        return;
      }
      showAlert({ message: data.Error || "No se pudo procesar la transferencia", color: "error" });
    } catch (e) {
      showAlert({ message: "No se pudo conectar con el servidor", color: "error" });
    } finally {
      marcarProcesando(transferencia.idtransaccion, false);
    }
  };

  const recibir = (transferencia) =>
    procesar(
      transferencia,
      "/api/transferenciabackend_procesarrecepciondocumentos",
      "Documentos recibidos correctamente"
    );

  // El diálogo queda abierto en "Procesando…" hasta que el backend responda.
  const rechazar = async () => {
    await procesar(
      rechazando,
      "/api/transferenciabackend_procesarrechazodocumentos",
      "Documentos devueltos correctamente"
    );
    setRechazando(null);
  };

  const columnas = [
    { field: "fechatransm", headerName: "Fecha envío", width: 140 },
    {
      field: "stransmisor",
      headerName: "Origen",
      flex: 0.9,
      minWidth: 120,
      valueGetter: (_v, row) => etiquetaSucursal(row.stransmisor),
    },
    {
      field: "chasis",
      headerName: "Chasis",
      width: 190,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.82rem" }}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      flex: 1,
      minWidth: 140,
      valueGetter: (_v, row) => {
        const x = extras[row.id] || {};
        return [x.marca, x.colore].filter(Boolean).join(" · ") || "—";
      },
    },
    {
      field: "documento",
      headerName: "Documento",
      width: 150,
      renderCell: (p) => {
        const t = tipoDocumento(p.row);
        return t ? (
          <Chip
            size="small"
            label={labelTipo(t)}
            sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
          />
        ) : (
          "—"
        );
      },
    },
    {
      field: "transportista",
      headerName: "Transportista",
      flex: 0.8,
      minWidth: 120,
      valueGetter: (_v, row) => (extras[row.id] || {}).transportista || "—",
    },
    {
      field: "comentransm",
      headerName: "Comentario",
      flex: 1,
      minWidth: 140,
      valueGetter: (_v, row) => (extras[row.id] || {}).comentransm || "—",
    },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (p) => {
        const ocupado = procesando.has(p.row.idtransaccion);
        return (
          <Box sx={{ display: "flex", gap: 0.8 }}>
            <Button
              size="small"
              disabled={ocupado}
              startIcon={
                ocupado ? <CircularProgress size={16} color="inherit" /> : <TaskAltOutlinedIcon />
              }
              onClick={() => recibir(p.row)}
              sx={btnSolid(FP.color.success, "#2E7D32")}
            >
              Recibir
            </Button>
            <Button
              size="small"
              disabled={ocupado}
              startIcon={<BlockOutlinedIcon />}
              onClick={() => setRechazando(p.row)}
              sx={btnOutline("#C62828")}
            >
              Rechazar
            </Button>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: FP.color.surface, minHeight: "100%" }}>
      <Box
        sx={{
          ...sectionBox,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          borderColor: FP.color.info,
          bgcolor: FP.color.infoSoft,
        }}
      >
        <InfoOutlinedIcon sx={{ color: FP.color.info }} />
        <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: FP.color.navy }}>
          Transferencias anteriores al sistema de envíos. Las nuevas se reciben desde Envíos /
          Recepción.
        </Typography>
      </Box>

      <Box sx={sectionBox}>
        <SectionTitle icon={Inventory2OutlinedIcon} color={FP.color.navy}>
          Recepción
        </SectionTitle>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary, mb: 0.6 }}>
              Sucursal de destino
            </Typography>
            <Chip
              label={sucursal || "Sin sucursal"}
              sx={{
                fontWeight: 700,
                color: "#1363DF",
                bgcolor: "#EAF3FF",
                border: "1px solid #D7E3F7",
              }}
            />
            <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted, mt: 1 }}>
              Los documentos recibidos se agregan al expediente del vehículo en esta sucursal.
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Comentario de recepción"
              placeholder="Se envía con la próxima recepción o rechazo"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fpMultilineInputSx}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={sectionBox}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <SectionTitle icon={LocalShippingOutlinedIcon} color="#E65100">
            En tránsito hacia mi sucursal
          </SectionTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            {transferencias.length > 0 && (
              <Chip
                size="small"
                label={`${transferencias.length} documento(s)`}
                sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
              />
            )}
            <Button
              startIcon={
                cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
              }
              onClick={cargar}
              disabled={cargando}
              sx={btnOutline(FP.color.navy)}
            >
              Actualizar
            </Button>
          </Box>
        </Box>

        {!cargando && transferencias.length === 0 ? (
          <EstadoVacio
            icono={Inventory2OutlinedIcon}
            titulo="No hay transferencias anteriores pendientes"
            subtitulo="Todo lo enviado con el sistema anterior ya fue recibido o rechazado. Las transferencias nuevas se reciben desde Envíos / Recepción."
          />
        ) : (
          <DataGrid
            autoHeight
            rows={transferencias}
            columns={columnas}
            loading={cargando}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={fpDataGridSx}
          />
        )}
      </Box>

      <ConfirmarDialog
        abierto={rechazando != null}
        titulo="Rechazar transferencia"
        destructivo
        textoConfirmar="Rechazar"
        cargando={rechazando != null && procesando.has(rechazando.idtransaccion)}
        onCerrar={() => setRechazando(null)}
        onConfirmar={rechazar}
      >
        {rechazando && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: "0.88rem", color: FP.color.textSecondary }}>
              Se eliminará la transferencia del documento{" "}
              <strong>{labelTipo(tipoDocumento(rechazando)) || "—"}</strong> del chasis{" "}
              <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                {rechazando.chasis}
              </Box>{" "}
              enviado desde {etiquetaSucursal(rechazando.stransmisor)}. El documento vuelve a quedar
              disponible en la sucursal de origen.
            </Typography>
          </Box>
        )}
      </ConfirmarDialog>
    </Box>
  );
}
Recepciondocumentos.propTypes = {
  tipo: PropTypes.string,
};
Recepciondocumentos.defaultProps = { tipo: "documentos" };

export default Recepciondocumentos;
